/* Distill — theme.js
   Vanilla JS only. Every module is idempotent and guarded, so sections can
   re-render in the Theme Editor without breaking. */

(function () {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- utils */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  function formatMoney(cents) {
    const format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    return format.replace(/\{\{\s*amount[^}]*\}\}/, value);
  }

  /* ------------------------------------------------------- scroll reveals */
  function initReveals(root = document) {
    const els = $$('.reveal:not(.is-visible)', root);
    if (!els.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('is-visible');
          io.unobserve(e.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.1 });
    els.forEach((el) => io.observe(el));
  }

  /* ---------------------------------------------------------------- header */
  function initHeader() {
    const header = $('.site-header');
    if (!header) return;
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    const toggle = $('[data-menu-toggle]');
    const menu = $('[data-mobile-menu]');
    const close = $('[data-menu-close]');
    if (toggle && menu) {
      toggle.addEventListener('click', () => {
        menu.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
      });
      const shut = () => {
        menu.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
      };
      if (close) close.addEventListener('click', shut);
      menu.addEventListener('click', (e) => { if (e.target.tagName === 'A') shut(); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') shut(); });
    }
  }

  /* ----------------------------------------------------------- cart drawer */
  const Cart = {
    open() {
      document.body.classList.add('drawer-open');
      const drawer = $('#CartDrawer');
      if (drawer) drawer.setAttribute('aria-hidden', 'false');
    },
    close() {
      document.body.classList.remove('drawer-open');
      const drawer = $('#CartDrawer');
      if (drawer) drawer.setAttribute('aria-hidden', 'true');
    },
    async refresh(openAfter) {
      try {
        const res = await fetch(window.theme.routes.cart_url + '?section_id=cart-drawer-items');
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('#CartDrawerInner');
        const current = $('#CartDrawerInner');
        if (fresh && current) current.replaceWith(fresh);

        const cartRes = await fetch(window.theme.routes.cart_url + '.js');
        const cart = await cartRes.json();
        $$('[data-cart-count]').forEach((el) => {
          el.textContent = cart.item_count;
          el.dataset.count = cart.item_count;
        });
        this.updateShipping(cart.total_price);
        if (openAfter) this.open();
      } catch (e) { /* fall back silently — cart page always works */ }
    },
    updateShipping(total) {
      const wrap = $('[data-ship-progress]');
      if (!wrap) return;
      const threshold = window.theme.freeShippingThreshold || 0;
      if (!threshold) { wrap.hidden = true; return; }
      const fill = $('.ship-progress__fill', wrap);
      const label = $('[data-ship-label]', wrap);
      const pct = Math.min(100, (total / threshold) * 100);
      if (fill) fill.style.width = pct + '%';
      if (label) {
        label.textContent = total >= threshold
          ? wrap.dataset.unlockedText
          : wrap.dataset.remainingText.replace('[amount]', formatMoney(threshold - total));
      }
      wrap.hidden = false;
    },
    async add(formData) {
      const res = await fetch(window.theme.routes.cart_add_url + '.js', {
        method: 'POST',
        body: formData
      });
      if (!res.ok) throw new Error('add-failed');
      await this.refresh(true);
    },
    async change(line, quantity) {
      await fetch(window.theme.routes.cart_change_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      await this.refresh(false);
    }
  };

  function initCart() {
    document.addEventListener('click', (e) => {
      const openBtn = e.target.closest('[data-cart-open]');
      if (openBtn) { e.preventDefault(); Cart.open(); return; }
      if (e.target.closest('[data-cart-close]') || e.target.closest('.drawer-overlay')) {
        Cart.close(); return;
      }
      const remove = e.target.closest('[data-cart-remove]');
      if (remove) { e.preventDefault(); Cart.change(Number(remove.dataset.line), 0); return; }
      const step = e.target.closest('[data-cart-qty]');
      if (step) {
        e.preventDefault();
        Cart.change(Number(step.dataset.line), Number(step.dataset.quantity));
      }
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') Cart.close(); });

    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[data-product-form]');
      if (!form) return;
      e.preventDefault();
      const btn = form.querySelector('[type="submit"]');
      const original = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = btn.dataset.addingText || original; }
      Cart.add(new FormData(form))
        .catch(() => form.submit())
        .finally(() => {
          if (btn) { btn.disabled = false; btn.textContent = original; }
        });
    });

    Cart.updateShippingInit = fetch(window.theme.routes.cart_url + '.js')
      .then((r) => r.json())
      .then((c) => Cart.updateShipping(c.total_price))
      .catch(() => {});
  }

  /* ------------------------------------------------- before/after sliders */
  function initBeforeAfter(root = document) {
    $$('.ba', root).forEach((el) => {
      if (el.dataset.init) return;
      el.dataset.init = '1';
      const set = (clientX) => {
        const rect = el.getBoundingClientRect();
        const pct = Math.min(96, Math.max(4, ((clientX - rect.left) / rect.width) * 100));
        el.style.setProperty('--ba-pos', pct + '%');
      };
      const move = (e) => set(e.touches ? e.touches[0].clientX : e.clientX);
      const start = (e) => {
        move(e);
        const end = () => {
          window.removeEventListener('pointermove', move);
          window.removeEventListener('pointerup', end);
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', end);
      };
      el.addEventListener('pointerdown', start);
      // Keyboard support
      const handle = $('.ba__handle', el);
      if (handle) {
        handle.tabIndex = 0;
        handle.setAttribute('role', 'slider');
        handle.setAttribute('aria-label', 'Compare before and after');
        handle.addEventListener('keydown', (e) => {
          const current = parseFloat(getComputedStyle(el).getPropertyValue('--ba-pos')) || 50;
          if (e.key === 'ArrowLeft') el.style.setProperty('--ba-pos', Math.max(4, current - 5) + '%');
          if (e.key === 'ArrowRight') el.style.setProperty('--ba-pos', Math.min(96, current + 5) + '%');
        });
      }
    });
  }

  /* ------------------------------------------------------------- hotspots */
  function initHotspots(root = document) {
    $$('[data-hotspots]', root).forEach((wrap) => {
      if (wrap.dataset.init) return;
      wrap.dataset.init = '1';
      const panelTitle = $('[data-hotspot-title]', wrap);
      const panelBody = $('[data-hotspot-body]', wrap);
      const spots = $$('.hotspot', wrap);
      const activate = (spot) => {
        spots.forEach((s) => s.setAttribute('aria-expanded', s === spot ? 'true' : 'false'));
        if (panelTitle) panelTitle.textContent = spot.dataset.title || '';
        if (panelBody) panelBody.textContent = spot.dataset.body || '';
      };
      spots.forEach((s) => s.addEventListener('click', () => activate(s)));
      if (spots[0]) activate(spots[0]);
    });
  }

  /* --------------------------------------------- water particles (canvas) */
  function initParticles(root = document) {
    if (reduceMotion) return;
    $$('canvas[data-particles]', root).forEach((canvas) => {
      if (canvas.dataset.init) return;
      canvas.dataset.init = '1';
      const ctx = canvas.getContext('2d');
      const color = canvas.dataset.color || '#7FA8B8';
      let w, h, drops = [], raf;

      function size() {
        const rect = canvas.parentElement.getBoundingClientRect();
        w = canvas.width = rect.width * devicePixelRatio;
        h = canvas.height = rect.height * devicePixelRatio;
      }
      function spawn() {
        drops = Array.from({ length: 46 }, () => ({
          x: Math.random() * w,
          y: Math.random() * h,
          r: (Math.random() * 2.2 + 0.8) * devicePixelRatio,
          v: (Math.random() * 0.5 + 0.18) * devicePixelRatio,
          o: Math.random() * 0.35 + 0.12
        }));
      }
      function tick() {
        ctx.clearRect(0, 0, w, h);
        drops.forEach((d) => {
          d.y += d.v;
          if (d.y - d.r > h) { d.y = -d.r; d.x = Math.random() * w; }
          ctx.globalAlpha = d.o;
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
          ctx.fill();
        });
        raf = requestAnimationFrame(tick);
      }
      const io = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) { cancelAnimationFrame(raf); tick(); }
        else cancelAnimationFrame(raf);
      });
      size(); spawn(); io.observe(canvas);
      window.addEventListener('resize', () => { size(); spawn(); }, { passive: true });
    });
  }

  /* -------------------------------------------------- product page extras */
  function initProduct() {
    // Sticky buy bar appears after the main CTA scrolls out of view
    const anchor = $('[data-buy-anchor]');
    const bar = $('.buy-bar');
    if (anchor && bar && 'IntersectionObserver' in window) {
      new IntersectionObserver(([e]) => {
        bar.classList.toggle('is-visible', !e.isIntersecting);
      }).observe(anchor);
      const barBtn = $('[data-buy-bar-submit]');
      if (barBtn) barBtn.addEventListener('click', () => {
        const form = $('form[data-product-form]');
        if (form) form.requestSubmit();
      });
    }

    // Tap-to-zoom on gallery images
    $$('.product-gallery__item').forEach((item) => {
      item.addEventListener('click', () => item.classList.toggle('is-zoomed'));
    });

    // Quantity steppers (product form)
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-qty-step]');
      if (!btn) return;
      const input = btn.parentElement.querySelector('input');
      if (!input) return;
      const next = Number(input.value || 1) + Number(btn.dataset.qtyStep);
      input.value = Math.max(1, next);
    });

    // Variant selection → update price + hidden id
    const form = $('form[data-product-form]');
    if (form && form.dataset.variants) {
      const variants = JSON.parse(form.dataset.variants);
      form.addEventListener('change', (e) => {
        if (!e.target.matches('[data-option-input]')) return;
        const selected = $$('[data-option-input]:checked', form).map((i) => i.value);
        const match = variants.find((v) => v.options.every((o, i) => o === selected[i]));
        if (!match) return;
        const idInput = form.querySelector('input[name="id"]');
        if (idInput) idInput.value = match.id;
        const priceEl = $('[data-price]');
        if (priceEl) priceEl.textContent = formatMoney(match.price);
        const btn = form.querySelector('[type="submit"]');
        if (btn) {
          btn.disabled = !match.available;
          btn.textContent = match.available ? btn.dataset.addText : btn.dataset.soldOutText;
        }
      });
    }
  }

  /* ------------------------------------------------------------------ init */
  function safe(fn) { try { fn(); } catch (e) { console.error('[distill]', e); } }

  /* --------------------------------------------------- video testimonials */
  function initVideos(scope) {
    var root = scope || document;
    var vids = root.querySelectorAll('.vid__media');
    if (!vids.length || !vids[0].tagName) return;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!reduce && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var v = entry.target;
          if (v.tagName !== 'VIDEO') return;
          if (entry.isIntersecting) {
            var p = v.play();
            if (p && p.catch) p.catch(function () {});
          } else {
            v.pause();
          }
        });
      }, { threshold: 0.45 });
      vids.forEach(function (v) { if (v.tagName === 'VIDEO') io.observe(v); });
    }
  }

  // Tap a clip to toggle sound
  document.addEventListener('click', function (e) {
    var card = e.target.closest('.vid');
    if (!card) return;
    var v = card.querySelector('video.vid__media');
    if (!v) return;
    if (e.target.closest('.vid__link')) return; // let the TikTok link through
    e.preventDefault();
    v.muted = !v.muted;
    if (!v.muted) {
      document.querySelectorAll('video.vid__media').forEach(function (other) {
        if (other !== v) other.muted = true;
      });
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    }
    var btn = card.querySelector('.vid__sound');
    if (btn) btn.textContent = v.muted ? 'SOUND OFF' : 'SOUND ON';
    document.querySelectorAll('.vid').forEach(function (c) {
      if (c === card) return;
      var b = c.querySelector('.vid__sound');
      if (b) b.textContent = 'SOUND OFF';
    });
  });


  /* ------------------------------------------------- bundle widget rescue */
  /* Bundle apps inject their markup wherever they like — sometimes inside the
     product form, sometimes appended to <body>. This finds the widget by
     signature (not by class name, which varies per app), moves it into the
     .app-slot above the buy buttons, and forces it to full width so text can
     never wrap one character per line. */
  function initBundleRescue() {
    var slot = document.querySelector('[data-app-slot]');
    if (!slot) return;

    var NAME_HINTS = /rapi|wrappy|bundle|volume|tier|quantity-break|qty-break|upsell|selling-plan|subscription|offer/i;
    var OURS = /app-slot|buy-stack|qty-row|variant-|product-info|product-layout|price|accordion|trust-|badge-|pay-row/;

    function looksLikeWidget(el) {
      if (!el || el.nodeType !== 1) return false;
      if (slot.contains(el)) return false;
      if (el.closest('[data-app-slot]')) return false;
      var id = (el.id || '') + ' ' + (el.className && el.className.toString ? el.className.toString() : '');
      if (OURS.test(id)) return false;
      if (!NAME_HINTS.test(id)) return false;
      // must actually contain selectable options
      var opts = el.querySelectorAll('input[type="radio"], [role="radio"], [data-variant-id], [data-bundle-option]');
      return opts.length >= 2;
    }

    function normalize(el) {
      // Container-level only. Never restyle the widget's internals —
      // forcing width on its children stretches radios and crushes text columns.
      el.style.setProperty('width', '100%', 'important');
      el.style.setProperty('max-width', '100%', 'important');
      el.style.setProperty('float', 'none', 'important');
    }

    function scan() {
      var candidates = document.querySelectorAll('div, section, aside, form > *');
      for (var i = 0; i < candidates.length; i++) {
        var el = candidates[i];
        if (!looksLikeWidget(el)) continue;
        // take the outermost matching ancestor
        var outer = el;
        var parent = el.parentElement;
        while (parent && looksLikeWidget(parent)) { outer = parent; parent = parent.parentElement; }
        if (!slot.contains(outer)) slot.appendChild(outer);
        normalize(outer);
        return true;
      }
      return false;
    }

    scan();

    // Widgets often mount late — watch for a while, then stop.
    if (!('MutationObserver' in window)) return;
    var obs = new MutationObserver(function () { scan(); });
    obs.observe(document.body, { childList: true, subtree: true });
    setTimeout(function () { obs.disconnect(); }, 20000);
  }

  function init() {
    safe(initReveals);
    safe(initHeader);
    safe(initCart);
    safe(initBeforeAfter);
    safe(initHotspots);
    safe(initParticles);
    safe(initProduct);
    safe(initVideos);
    safe(initBundleRescue);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Theme Editor section reloads
  document.addEventListener('shopify:section:load', (e) => {
    initReveals(e.target);
    initBeforeAfter(e.target);
    initHotspots(e.target);
    initParticles(e.target);
    initVideos(e.target);
  });

  // Quick add (offer cards, upsells)
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    var id = btn.getAttribute('data-quick-add');
    var qty = parseInt(btn.getAttribute('data-quick-qty') || '1', 10);
    var fd = new FormData();
    fd.append('id', id);
    fd.append('quantity', qty);
    var label = btn.textContent;
    btn.disabled = true; btn.textContent = 'Adding…';
    Cart.add(fd).then(function () {
      btn.textContent = 'Added ✓';
      setTimeout(function () { btn.textContent = label; btn.disabled = false; }, 1600);
    }).catch(function () { btn.textContent = label; btn.disabled = false; });
  });

})();
